import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { SESSION_TYPE, STORAGE_KEYS } from "constants/appConstant";
import {
  getAuthInfo,
  onAppleLogin,
  onFacebookLogin,
  onForgotPassword,
  onGetCreditBalance,
  onGoogleLogin,
  onLogin,
  onLogout,
  onRequestAuthCode,
  onRequestQRCode,
  onResetPassword,
  onVerifyEmail,
  onVerifyTwoFactorAuth,
} from "services/apis/auth";
import StorageHelper from "utils/storeHelper";

// import { doGetNoticeDisplay } from "./notice";

export const doGetCreditBalance = createAsyncThunk(
  "auth/doGetCreditBalance",
  async params => {
    const result = await onGetCreditBalance(params);
    if (!result) {
      return;
    }
    return result;
  }
);

export const doVerifyEmail = createAsyncThunk(
  "auth/doVerifyEmail",
  async params => {
    const result = await onVerifyEmail(params);
    if (!result) {
      return;
    }
    return result;
  }
);

export const doVerifyTwoFactorAuth = createAsyncThunk(
  "auth/doVerifyTwoFactorAuth",
  async params => {
    const result = await onVerifyTwoFactorAuth(params);
    if (!result) {
      return;
    }
    // await dispatch(getMe());
    return result;
  }
);

export const doRequestTwoFactorAuth = createAsyncThunk(
  "auth/doRequestTwoFactorAuth",
  async params => {
    const result = await onRequestAuthCode(params);
    if (!result) {
      return;
    }
    return result;
  }
);

export const doRequestQRCode = createAsyncThunk(
  "auth/doRequestQRCode",
  async params => {
    const result = await onRequestQRCode(params);
    if (!result) {
      return;
    }
    return result;
  }
);

export const doResetPassword = createAsyncThunk(
  "auth/doResetPassword",
  async params => {
    const result = await onResetPassword(params);
    if (!result) {
      return;
    }
    return result;
  }
);

// Apple Login
export const appleLogin = createAsyncThunk(
  "auth/appleLogin",
  async (params, { dispatch }) => {
    const result = await onAppleLogin(params);
    if (!result) {
      return;
    }
    if (result.data.sessionType === SESSION_TYPE.PRIMARY) {
      const res = result.data;
      StorageHelper.setCookie(STORAGE_KEYS.token, res.authToken, {
        maxAge: res.expireTime,
      });
      await dispatch(getMe());
      // dispatch(doGetNoticeDisplay());
    }

    return result;
  }
);
// Facebook Login
export const facebookLogin = createAsyncThunk(
  "auth/facebookLogin",
  async (params, { dispatch }) => {
    const result = await onFacebookLogin(params);
    if (!result) {
      return;
    }
    if (result.data.sessionType === SESSION_TYPE.PRIMARY) {
      const res = result.data;
      StorageHelper.setCookie(STORAGE_KEYS.token, res.authToken, {
        maxAge: res.expireTime,
      });
      await dispatch(getMe());
      // dispatch(doGetNoticeDisplay());
    }

    return result;
  }
);

// Google Login
export const googleLogin = createAsyncThunk(
  "auth/googleLogin",
  async (params, { dispatch }) => {
    const result = await onGoogleLogin(params);
    if (!result) {
      return;
    }
    if (result.data.sessionType === SESSION_TYPE.PRIMARY) {
      const res = result.data;
      StorageHelper.setCookie(STORAGE_KEYS.token, res.authToken, {
        maxAge: res.expireTime,
      });
      await dispatch(getMe());
      // dispatch(doGetNoticeDisplay());
      // const { payload } = await dispatch(getMe());
      // if (typeof payload === "object") {
      //   const { type, workspaceRole } = payload;
      //   if (type === workspaceRole) {
      //     await dispatch(changeLayout("OPERATOR"));
      //   } else {
      //     await dispatch(changeLayout("ADMIN"));
      //   }
      // }
    }
    return result;
  }
);

// Sign In
export const login = createAsyncThunk(
  "auth/login",
  async (params, { dispatch }) => {
    const result = await onLogin(params);
    if (!result) {
      return;
    }
    if (result.data.sessionType === SESSION_TYPE.PRIMARY) {
      StorageHelper.setCookie(STORAGE_KEYS.token, result.data.authToken, {
        maxAge: result.data.expireTime,
      });
      await dispatch(getMe());
      // dispatch(doGetNoticeDisplay());
    }
    return result;
  }
);

// Logout
export const logout = createAsyncThunk("auth/logout", async () => {
  const result = await onLogout();
  if (!result) {
    return;
  }
  await StorageHelper.removeCookie(STORAGE_KEYS.token);
  StorageHelper.removeLocalItem(STORAGE_KEYS.user);

  return result.data;
});

// Forgot password
export const forgotPassword = createAsyncThunk(
  "auth/forgotPassword",
  async params => {
    const result = await onForgotPassword(params);
    if (!result) {
      return;
    }
    return result;
  }
);

// Get Auth Info
export const getMe = createAsyncThunk("auth/getMe", async () => {
  const result = await getAuthInfo();
  if (!result) {
    return;
  }
  StorageHelper.setLocalObject(STORAGE_KEYS.user, result.data || {});
  return result.data;
});

const _isAuthenticated = !!StorageHelper.getCookie(STORAGE_KEYS.token);

const initialState = {
  isAuthenticated: _isAuthenticated,
  isSessionValidating: _isAuthenticated,
  user: StorageHelper.getLocalObject(STORAGE_KEYS.user) || {},
  QRCode: null,
  isSaving: false,
  twoFactorAuthRes: {},
  email: null,
};

const slice = createSlice({
  name: "auth",
  initialState: initialState,
  reducers: {
    clearAuthData: state => {
      if (state.isAuthenticated) {
        state.user = {};
        state.isAuthenticated = false;
      }
    },
    setEmail: (state, action) => {
      state.email = action.payload;
    },
  },
  extraReducers: {
    [login.fulfilled]: (state, action) => {
      state.twoFactorAuthRes = action.payload.data;
    },
    [googleLogin.fulfilled]: (state, action) => {
      state.twoFactorAuthRes = action.payload.data;
    },
    [facebookLogin.fulfilled]: (state, action) => {
      state.twoFactorAuthRes = action.payload.data;
    },
    [appleLogin.fulfilled]: (state, action) => {
      state.twoFactorAuthRes = action.payload.data;
    },
    [getMe.fulfilled]: (state, action) => {
      state.user = action.payload || {};
      state.isAuthenticated = action.payload ? true : false;
    },
    [getMe.rejected]: state => {
      state.user = {};
      state.isAuthenticated = false;
    },
    [logout.fulfilled]: state => {
      state.user = {};
      state.isAuthenticated = false;
    },
    [doRequestQRCode.fulfilled]: (state, action) => {
      state.QRCode = action.payload;
    },
    [doVerifyTwoFactorAuth.pending]: state => {
      state.isSaving = true;
    },
    [doVerifyTwoFactorAuth.fulfilled]: state => {
      state.isSaving = false;
    },
    [doVerifyTwoFactorAuth.rejected]: state => {
      state.isSaving = false;
    },
  },
});

const { reducer, actions } = slice;
export const { clearAuthData, setEmail } = actions;
export default reducer;

export const AuthInfo = createSelector(
  state => state.auth.user || StorageHelper.getLocalObject(STORAGE_KEYS.user),
  authInfo => authInfo
);

export const isLoggedIn = createSelector(
  state => state.auth.isAuthenticated,
  data => data
);

export const getQRCode = createSelector(
  state => state.auth.QRCode,
  QRCode => QRCode
);

export const getTwoFactorAuthRes = createSelector(
  state => state.auth.twoFactorAuthRes,
  twoFactorAuthRes => twoFactorAuthRes
);

export const getWorkspaceId = createSelector(
  state => state.auth.user.workspaceId,
  authInfo => authInfo
);

export const getWorkspaceRole = createSelector(
  state => state.auth.user.workspaceRole,
  authInfo => authInfo
);

export const getEmail = createSelector(
  state => state.auth.email,
  email => email
);
